import React, { useState, useRef, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Input,
  Badge,
  Card,
  CardBody,
  FormGroup,
  Label,
} from "reactstrap";
import Select from "react-select";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import CampaignAssignService from "../../../services/OrderingServices/CampaignAssignService";

// Skeleton Card Component
const SkeletonCard = () => (
  <Col xs="12" sm="6" md="4" lg="3">
    <Card className="border-0 rounded-3 shadow-sm">
      <CardBody className="d-flex flex-column p-2">
        <div
          className="skeleton skeleton-image"
          style={{ height: "120px", marginBottom: "8px" }}
        />
        <div
          className="skeleton skeleton-text"
          style={{ height: "15px", marginBottom: "8px" }}
        />
        <div className="skeleton skeleton-text" style={{ height: "12px" }} />
      </CardBody>
    </Card>
  </Col>
);

const AddMediaModalSidePanel = ({
  modalOpen,
  setModalOpen,
  modalLoading,
  modalAssigning,
  isReorderMode,
  modalSelectedAds = [],
  allAds = [],
  hasMore,
  currentPage,
  totalPages,
  searchText,
  setSearchText,
  fetchAds,
  mediaType,
  adFormat,
  advertisers = [],
  tags = [],
  handleAddMedia,
  handleBackToSelection,
  handleProceedToReorder,
  toggleModalAdSelection,
  getMediaUrl,
  LazyImage,
  draggedItem,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleDragEnd,
  campaign,
  onMediaUpdated,
}) => {
  const [filters, setFilters] = useState({
    adName: "",
    mediaType: "",
    adFormat: "",
    advertiserName: "",
    tags: [],
    minFileSize: 0,
    maxFileSize: 50000,
    status: "",
    durationSeconds: "",
    fromDate: "",
    toDate: "",
    recentlyUploaded: false,
  });

  // State for reordering ads
  const [reorderAds, setReorderAds] = useState([]);
  console.log("🚀 ~ AddMediaModalSidePanel ~ reorderAds:", reorderAds);
  const [reorderDraggedItem, setReorderDraggedItem] = useState(null);

  // State for storing duration of selected ads
  const [adDurations, setAdDurations] = useState({});

  const debounceTimerRef = useRef(null);

  // Helper function to get the correct image URL (thumbnail for videos, mediaPath for images)
  const getDisplayImage = (ad) => {
    // For videos with thumbnails, use the thumbnail
    if (ad.thumbnailFile) {
      return getMediaUrl({ ...ad, mediaPath: ad.thumbnailFile });
    }
    // For images or videos without thumbnails, use the mediaPath
    return getMediaUrl(ad);
  };

  // Format file size to human-readable format (B, KB, MB, GB)
  const formatFileSize = (kb) => {
    if (kb === 0) return "0 B";
    const bytes = kb * 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = (bytes / Math.pow(1024, i)).toFixed(2);
    return `${size} ${sizes[i]}`;
  };

  // Format date for API (ISO datetime string)
  const formatDateForApi = (date) => {
    if (!date) return "";
    if (typeof date === "string") {
      // If it's a string like "Y-m-d", convert to ISO datetime
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return new Date(date).toISOString();
      }
      return date;
    }
    // If it's a Date object, convert to ISO string
    return date.toISOString();
  };

  // Build clean filter object for API
  const buildApiFilters = (filterObj) => {
    return {
      adName: filterObj.adName || undefined,
      mediaType: filterObj.mediaType || undefined,
      adFormat: filterObj.adFormat || undefined,
      advertiserName: filterObj.advertiserName || undefined,
      tags:
        filterObj.tags && filterObj.tags.length > 0
          ? filterObj.tags
          : undefined,
      minFileSize:
        filterObj.minFileSize > 0 ? filterObj.minFileSize : undefined, // Already in KB, API expects bytes internally
      maxFileSize:
        filterObj.maxFileSize > 0 ? filterObj.maxFileSize : undefined, // Already in KB, API expects bytes internally
      status: filterObj.status !== "" ? filterObj.status : undefined,
      durationSeconds: filterObj.durationSeconds
        ? parseInt(filterObj.durationSeconds)
        : undefined,
      fromDate: filterObj.fromDate
        ? formatDateForApi(filterObj.fromDate)
        : undefined,
      toDate: filterObj.toDate ? formatDateForApi(filterObj.toDate) : undefined,
      recentlyUploaded: filterObj.recentlyUploaded || undefined,
      pageNumber: 1,
      pageSize: 20,
    };
  };

  // Debounced fetch to prevent multiple calls
  const debouncedFetchAds = useCallback(
    (filterObj) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        const apiFilters = buildApiFilters(filterObj);
        fetchAds(1, apiFilters);
      }, 300); // 300ms debounce
    },
    [fetchAds],
  );

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
    debouncedFetchAds(updatedFilters);
  };

  // Handle file size slider changes during drag (just update state)
  const handleFileRangeChange = (values) => {
    setFilters({
      ...filters,
      minFileSize: values[0],
      maxFileSize: values[1],
    });
  };

  // Handle file size slider after drag ends (fetch data)
  const handleFileRangeAfterChange = (values) => {
    const updatedFilters = {
      ...filters,
      minFileSize: values[0],
      maxFileSize: values[1],
    };
    setFilters(updatedFilters);
    debouncedFetchAds(updatedFilters);
  };

  const handleDateRangeChange = (selectedDates) => {
    console.log(
      "Selected Dates:",
      selectedDates,
      "Length:",
      selectedDates.length,
    );

    const updatedFilters = {
      ...filters,
      fromDate: selectedDates[0] ? selectedDates[0] : "",
      toDate: selectedDates[1] ? selectedDates[1] : "",
    };

    console.log("Updated Filters:", updatedFilters);
    setFilters(updatedFilters);

    // Fetch when both dates are selected
    if (selectedDates.length === 2 && selectedDates[0] && selectedDates[1]) {
      console.log("Both dates selected, fetching...");

      // Clear debounce and fetch immediately for date changes
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      const apiFilters = buildApiFilters(updatedFilters);
      console.log("API Filters with dates:", apiFilters);
      fetchAds(1, apiFilters);
    }
  };

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      const apiFilters = buildApiFilters(filters);
      fetchAds(currentPage + 1, apiFilters, true); // true for append flag
    }
  };

  // Merge campaign ads and selected ads, avoiding duplicates
  const mergedSelectedAds = React.useMemo(() => {
    if (!campaign || !campaign.ads) return modalSelectedAds;

    const existingIds = new Set(modalSelectedAds.map((ad) => ad.id));
    const campaignAdsToAdd = campaign.ads.filter(
      (ad) => !existingIds.has(ad.id),
    );

    return [...modalSelectedAds, ...campaignAdsToAdd];
  }, [modalSelectedAds, campaign]);

  // Initialize reorderAds when entering reorder mode or when mergedSelectedAds changes
  React.useEffect(() => {
    if (isReorderMode) {
      setReorderAds([...mergedSelectedAds]);
      setReorderDraggedItem(null);
    }
  }, [isReorderMode, mergedSelectedAds]);

  // Drag handlers for reordering
  const handleReorderDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = "move";
    setReorderDraggedItem(index);
  };

  const handleReorderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleReorderDrop = (e, index) => {
    e.preventDefault();
    if (reorderDraggedItem === null || reorderDraggedItem === index) return;

    const updatedAds = [...reorderAds];
    const [movedItem] = updatedAds.splice(reorderDraggedItem, 1);
    updatedAds.splice(index, 0, movedItem);

    setReorderAds(updatedAds);
    setReorderDraggedItem(null);
  };

  const handleReorderDragEnd = () => {
    setReorderDraggedItem(null);
  };

  // Wrapper for handleAddMedia to pass reordered ads
  const handleAddMediaWithReorder = async () => {
    try {
      // Step 1: Assign media first
      await handleAddMedia(reorderAds);

      // Step 2: Update duration for each ad that has a duration set
      const durationPromises = Object.entries(adDurations)
        .filter(([_, duration]) => duration !== "" && duration !== null)
        .map(([adId, duration]) => {
          return CampaignAssignService.updateAdDuration(
            campaign?.id,
            adId,
            parseInt(duration),
          );
        });

      if (durationPromises.length > 0) {
        await Promise.all(durationPromises);
      }

      // Step 3: Reorder the ads
      const reorderedAdIds = reorderAds.map((ad) => ad.id);
      await CampaignAssignService.reorderCampaignAds(
        campaign?.id,
        reorderedAdIds,
      );

      // Step 4: Refetch media list to show updated duration in the media tab
      const apiFilters = buildApiFilters(filters);
      await fetchAds(1, apiFilters);

      // Step 5: Call parent callback to refresh campaign data
      if (onMediaUpdated) {
        await onMediaUpdated();
      }

      // Step 6: Reset duration state and close modal
      setAdDurations({});
      setModalOpen(false);
    } catch (error) {
      console.error("Error in media assignment flow:", error);
      throw error;
    }
  };

  // Handle duration input change
  const handleDurationChange = (adId, value) => {
    setAdDurations((prev) => ({
      ...prev,
      [adId]: value,
    }));
  };

  return (
    <Modal
      isOpen={modalOpen}
      toggle={() => !modalLoading && setModalOpen(false)}
      //   size="xl"
      centered
      style={{ maxWidth: "80vw" }}
    >
      <ModalHeader toggle={() => !modalLoading && setModalOpen(false)}>
        Add Media to Campaign
      </ModalHeader>

      <ModalBody
        style={{
          height: "70vh",
          maxHeight: "70vh",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {modalLoading ? (
          <Row style={{ height: "100%", width: "100%", margin: 0 }}>
            {/* --- Filter Column (Left Side) --- */}
            <Col
              md="3"
              className="border-end pe-3"
              style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}
            >
              <FormGroup>
                <Label>Search Ad Name</Label>
                <Input
                  type="text"
                  value={filters.adName}
                  placeholder="Search ad name..."
                  onChange={(e) => handleFilterChange("adName", e.target.value)}
                  disabled={modalLoading}
                />
              </FormGroup>

              <FormGroup>
                <Label>Media Type</Label>
                <Select
                  options={[
                    { value: 1, label: "Image" },
                    { value: 2, label: "Video" },
                  ]}
                  value={
                    filters.mediaType
                      ? {
                          value: filters.mediaType,
                          label: filters.mediaType === 1 ? "Image" : "Video",
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleFilterChange(
                      "mediaType",
                      selected ? selected.value : "",
                    )
                  }
                  isClearable
                  isDisabled={modalLoading}
                  placeholder="Select Media Type"
                />
              </FormGroup>

              <FormGroup>
                <Label>Ad Format</Label>
                <Select
                  options={[
                    { value: 1, label: "Full Screen" },
                    { value: 2, label: "Banner" },
                  ]}
                  value={
                    filters.adFormat
                      ? {
                          value: filters.adFormat,
                          label:
                            filters.adFormat === 1 ? "Full Screen" : "Banner",
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleFilterChange(
                      "adFormat",
                      selected ? selected.value : "",
                    )
                  }
                  isClearable
                  isDisabled={modalLoading}
                  placeholder="Select Ad Format"
                />
              </FormGroup>

              <FormGroup>
                <Label>Advertiser</Label>
                <Select
                  options={advertisers.map((adv) => ({
                    value: adv,
                    label: adv,
                  }))}
                  value={
                    filters.advertiserName
                      ? {
                          value: filters.advertiserName,
                          label: filters.advertiserName,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleFilterChange(
                      "advertiserName",
                      selected ? selected.value : "",
                    )
                  }
                  isClearable
                  isDisabled={modalLoading}
                  placeholder="Select Advertiser"
                />
              </FormGroup>

              <FormGroup>
                <Label>Tags</Label>
                <Select
                  options={tags.map((t) => ({ value: t, label: t }))}
                  value={filters.tags.map((t) => ({ value: t, label: t }))}
                  onChange={(selected) =>
                    handleFilterChange(
                      "tags",
                      selected ? selected.map((s) => s.value) : [],
                    )
                  }
                  isMulti
                  isClearable
                  isDisabled={modalLoading}
                  placeholder="Select Tags"
                />
              </FormGroup>

              <FormGroup>
                <Label>File Size Range</Label>

                <div className="px-2 mt-3">
                  <Slider
                    range
                    min={0}
                    max={50000}
                    step={100}
                    marks={{
                      0: "0",
                      1024: "1MB",
                      10240: "10MB",
                      25600: "25MB",
                      50000: "50MB",
                    }}
                    value={[
                      filters.minFileSize || 0,
                      filters.maxFileSize || 50000,
                    ]}
                    onChange={handleFileRangeChange}
                    onAfterChange={handleFileRangeAfterChange}
                    disabled={modalLoading}
                  />
                </div>

                <div className="text-center mt-4">
                  <small className="text-primary fw-bold">
                    {formatFileSize(filters.minFileSize || 0)} →{" "}
                    {formatFileSize(filters.maxFileSize || 50000)}
                  </small>
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Date Range</Label>

                <Flatpickr
                  options={{
                    mode: "range",
                    minDate: "today",
                    dateFormat: "Y-m-d",
                    closeOnSelect: false,
                  }}
                  value={[filters.fromDate || null, filters.toDate || null]}
                  onChange={handleDateRangeChange}
                  className="form-control"
                  placeholder="Select date range"
                  disabled={modalLoading}
                />
              </FormGroup>

              <Button
                color="link"
                onClick={() => {
                  const resetFilters = {
                    adName: "",
                    mediaType: "",
                    adFormat: "",
                    advertiserName: "",
                    tags: [],
                    minFileSize: 0,
                    maxFileSize: 50000,
                    status: "",
                    durationSeconds: "",
                    fromDate: "",
                    toDate: "",
                    recentlyUploaded: false,
                  };
                  setFilters(resetFilters);
                  setSearchText("");
                  const apiFilters = buildApiFilters(resetFilters);
                  fetchAds(1, apiFilters);
                }}
                disabled={modalLoading}
              >
                Clear All Filters
              </Button>
            </Col>

            {/* --- Skeleton Loading Grid (Right Side) --- */}
            <Col
              md="9"
              mb="0"
              style={{
                height: "100%",
                maxHeight: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Row className="g-3">
                {[...Array(8)].map((_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </Row>
            </Col>
          </Row>
        ) : isReorderMode ? (
          <>
            <h5 className="mb-3">Reorder Selected Media</h5>
            <Row className="g-3">
              {reorderAds.map((ad, index) => (
                <Col
                  key={ad.id}
                  xs="12"
                  sm="4"
                  md="4"
                  lg
                  style={{
                    flex: "0 0 20%",
                    maxWidth: "20%",
                  }}
                >
                  <Card
                    draggable
                    onDragStart={(e) => handleReorderDragStart(e, index)}
                    onDragOver={handleReorderDragOver}
                    onDrop={(e) => handleReorderDrop(e, index)}
                    onDragEnd={handleReorderDragEnd}
                    style={{
                      cursor: "move",
                      opacity: reorderDraggedItem === index ? 0.5 : 1,
                    }}
                  >
                    <div className="position-absolute top-0 start-0 p-2">
                      <Badge color="primary" pill>
                        {index + 1}
                      </Badge>
                    </div>

                    <LazyImage
                      src={getDisplayImage(ad)}
                      alt={ad.title}
                      className="w-100"
                      style={{ height: "120px", objectFit: "cover" }}
                    />

                    <CardBody>
                      <div className="d-flex align-item-start justify-content-between gap-2">
                        <div className="flex-grow-1">
                          <h6 className="text-truncate mb-1">{ad.title}</h6>
                          <small className="text-muted">
                            {ad.advertiserName}
                          </small>
                        </div>

                        <FormGroup
                          className="mb-0"
                          style={{ width: "120px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* <Label for={`duration-${ad.id}`} className="mb-1">
                            Duration (sec)
                          </Label> */}
                          <Input
                            id={`duration-${ad.id}`}
                            type="number"
                            size="sm"
                            placeholder="Sec"
                            // value={adDurations[ad.id] || ""}
                            value={
                              adDurations[ad.id] ??
                              ad.durationSeconds ??
                              ad.duration
                            }
                            onChange={(e) =>
                              handleDurationChange(ad.id, e.target.value)
                            }
                            min="0"
                          />
                        </FormGroup>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <Row style={{ height: "100%", width: "100%", margin: 0 }}>
            {/* --- Side Panel Filters --- */}
            <Col
              md="3"
              className="border-end pe-3"
              style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}
            >
              <FormGroup>
                <Label>Search Ad Name</Label>
                <Input
                  type="text"
                  value={filters.adName}
                  placeholder="Search ad name..."
                  onChange={(e) => handleFilterChange("adName", e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Media Type</Label>
                <Select
                  options={[
                    { value: 1, label: "Image" },
                    { value: 2, label: "Video" },
                  ]}
                  value={
                    filters.mediaType
                      ? {
                          value: filters.mediaType,
                          label: filters.mediaType === 1 ? "Image" : "Video",
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleFilterChange(
                      "mediaType",
                      selected ? selected.value : "",
                    )
                  }
                  isClearable
                  placeholder="Select Media Type"
                />
              </FormGroup>

              <FormGroup>
                <Label>Ad Format</Label>
                <Select
                  options={[
                    { value: 1, label: "Full Screen" },
                    { value: 2, label: "Banner" },
                  ]}
                  value={
                    filters.adFormat
                      ? {
                          value: filters.adFormat,
                          label:
                            filters.adFormat === 1 ? "Full Screen" : "Banner",
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleFilterChange(
                      "adFormat",
                      selected ? selected.value : "",
                    )
                  }
                  isClearable
                  placeholder="Select Ad Format"
                />
              </FormGroup>

              <FormGroup>
                <Label>Advertiser</Label>
                <Select
                  options={advertisers.map((adv) => ({
                    value: adv,
                    label: adv,
                  }))}
                  value={
                    filters.advertiserName
                      ? {
                          value: filters.advertiserName,
                          label: filters.advertiserName,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleFilterChange(
                      "advertiserName",
                      selected ? selected.value : "",
                    )
                  }
                  isClearable
                  placeholder="Select Advertiser"
                />
              </FormGroup>

              <FormGroup>
                <Label>Tags</Label>
                <Select
                  options={tags.map((t) => ({ value: t, label: t }))}
                  value={filters.tags.map((t) => ({ value: t, label: t }))}
                  onChange={(selected) =>
                    handleFilterChange(
                      "tags",
                      selected ? selected.map((s) => s.value) : [],
                    )
                  }
                  isMulti
                  isClearable
                  placeholder="Select Tags"
                />
              </FormGroup>

              <FormGroup>
                <Label>File Size Range</Label>

                <div className="px-2 mt-3">
                  <Slider
                    range
                    min={0}
                    max={50000}
                    step={100}
                    marks={{
                      0: "0",
                      1024: "1MB",
                      10240: "10MB",
                      25600: "25MB",
                      50000: "50MB",
                    }}
                    value={[
                      filters.minFileSize || 0,
                      filters.maxFileSize || 50000,
                    ]}
                    onChange={handleFileRangeChange}
                    onAfterChange={handleFileRangeAfterChange}
                  />
                </div>

                <div className="text-center mt-4">
                  <small className="text-primary fw-bold">
                    {formatFileSize(filters.minFileSize || 0)} →{" "}
                    {formatFileSize(filters.maxFileSize || 50000)}
                  </small>
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Date Range</Label>

                <Flatpickr
                  options={{
                    mode: "range",
                    minDate: "today",
                    dateFormat: "Y-m-d",
                    closeOnSelect: false,
                  }}
                  value={[filters.fromDate || null, filters.toDate || null]}
                  onChange={handleDateRangeChange}
                  className="form-control"
                  placeholder="Select date range"
                />
              </FormGroup>

              <Button
                color="link"
                onClick={() => {
                  const resetFilters = {
                    adName: "",
                    mediaType: "",
                    adFormat: "",
                    advertiserName: "",
                    tags: [],
                    minFileSize: 0,
                    maxFileSize: 50000,
                    status: "",
                    durationSeconds: "",
                    fromDate: "",
                    toDate: "",
                    recentlyUploaded: false,
                  };
                  setFilters(resetFilters);
                  setSearchText("");
                  const apiFilters = buildApiFilters(resetFilters);
                  fetchAds(1, apiFilters);
                }}
              >
                Clear All Filters
              </Button>
            </Col>

            {/* --- Media Grid --- */}
            <Col
              md="9"
              mb="0"
              style={{
                height: "100%",
                maxHeight: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {allAds.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No media available</p>
                </div>
              ) : (
                <Row className="g-3">
                  {allAds.map((ad) => {
                    const isSelected = modalSelectedAds.some(
                      (a) => a.id === ad.id,
                    );
                    return (
                      <Col
                        key={ad.id}
                        xs="12"
                        sm="6"
                        md="4"
                        lg="3"
                        // style={{
                        //   flex: "0 0 20%",
                        //   maxWidth: "20%",
                        // }}
                      >
                        <Card
                          onClick={() => toggleModalAdSelection(ad)}
                          className={
                            isSelected ? "border-primary shadow-sm" : ""
                          }
                          style={{ cursor: "pointer", position: "relative" }}
                        >
                          {isSelected && (
                            <div className="position-absolute top-0 end-0 p-1">
                              <Badge color="success" pill>
                                ✓
                              </Badge>
                            </div>
                          )}

                          <LazyImage
                            src={getDisplayImage(ad)}
                            alt={ad.title}
                            className="w-100"
                            style={{ height: "120px", objectFit: "cover" }}
                          />

                          <CardBody>
                            <div className="d-flex align-items-start justify-content-between gap-2">
                              {/* Left side - Title & Advertiser */}
                              <div className="flex-grow-1">
                                <h6 className="text-truncate mb-1">
                                  {ad.title} ({ad.size}mb)
                                </h6>
                                <small className="text-muted">
                                  {ad.advertiserName}
                                </small>
                              </div>

                              {/* Right side - Duration Input */}
                              {isSelected && (
                                <FormGroup
                                  className="mb-0"
                                  style={{ width: "120px" }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Input
                                    id={`duration-grid-${ad.id}`}
                                    value={
                                      adDurations[ad.id] ?? ad.durationSeconds ?? ad.duration
                                    }
                                    type="number"
                                    size="sm"
                                    placeholder="Sec"
                                    // value={adDurations[ad.id] || ""}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleDurationChange(
                                        ad.id,
                                        e.target.value,
                                      );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    min="0"
                                  />
                                </FormGroup>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    );
                  })}

                  {hasMore && (
                    <div className="text-center mt-2" style={{ flexShrink: 0 }}>
                      <Button
                        color="primary"
                        outline
                        onClick={handleLoadMore}
                        disabled={modalLoading}
                      >
                        Load More ({currentPage} of {totalPages})
                      </Button>
                    </div>
                  )}
                </Row>
              )}
            </Col>
          </Row>
        )}
      </ModalBody>

      <ModalFooter>
        {isReorderMode ? (
          <>
            <span className="text-muted me-3">
              {reorderAds.length} media selected ({modalSelectedAds.length} new,{" "}
              {reorderAds.length - modalSelectedAds.length} existing)
            </span>
            <Button
              color="secondary"
              onClick={handleBackToSelection}
              disabled={modalAssigning}
            >
              ← Previous
            </Button>

            <Button
              color="primary"
              onClick={handleAddMediaWithReorder}
              disabled={modalAssigning}
            >
              {modalAssigning ? "Assigning..." : "Assign Media"}
            </Button>
          </>
        ) : (
          <>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>

            <Button
              color="primary"
              onClick={handleProceedToReorder}
              disabled={modalSelectedAds.length === 0}
            >
              Reorder Media ({modalSelectedAds.length})
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AddMediaModalSidePanel;
